/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2023 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { EnhanceSpeechTrackId } from "components/EnhancedSpeech/Utilities";
import { updateIfExists } from "reducers/utilities/UpdateIfExists";
import { EnhancementObject } from "components/EnhancedSpeech/useEnhanceSpeech";
import { Dispatch } from "react";

export type UseEnhanceSpeechActions =
  | { type: "REMOVE_ENHANCEMENT"; trackId: EnhanceSpeechTrackId }
  | { type: "ADD_ENHANCEMENT"; enhancement: EnhancementObject }
  | { type: "LOAD_ENHANCEMENTS"; enhancements: EnhancementObject[] }
  | { type: "UPDATE_ENHANCEMENT"; enhancement: EnhancementObject }
  | { type: "SET_ENHANCEMENT_DELETING"; trackId: EnhanceSpeechTrackId }
  | { type: "UNSET_ENHANCEMENT_DELETING"; trackId: EnhanceSpeechTrackId }
  | {
      type: "UPDATE_ENHANCEMENT_UPLOAD_PROGRESS";
      trackId: EnhanceSpeechTrackId;
      percentComplete: number;
    };
export const enhancementsReducer = (
  state: EnhancementObject[],
  action: UseEnhanceSpeechActions
): EnhancementObject[] => {
  switch (action.type) {
    case "REMOVE_ENHANCEMENT":
      return state.filter(
        (enhancement) => enhancement.track.id !== action.trackId
      );
    case "ADD_ENHANCEMENT":
      return [action.enhancement, ...state];
    case "LOAD_ENHANCEMENTS":
      return action.enhancements;
    case "UPDATE_ENHANCEMENT":
      return updateIfExists(
        state,
        action.enhancement.track.id,
        action.enhancement,
        (enhancement) => enhancement.track.id
      );
    case "SET_ENHANCEMENT_DELETING":
      return updateIfExists(
        state,
        action.trackId,
        {
          isDeleting: true,
        },
        (enhancement) => enhancement.track.id
      );
    case "UNSET_ENHANCEMENT_DELETING":
      return updateIfExists(
        state,
        action.trackId,
        {
          isDeleting: false,
        },
        (enhancement) => enhancement.track.id
      );
    case "UPDATE_ENHANCEMENT_UPLOAD_PROGRESS":
      return updateIfExists(
        state,
        action.trackId,
        {
          percentUploaded: action.percentComplete,
        },
        (enhancement) => enhancement.track.id
      );
  }
};
export type UseEnhanceDispatch = Dispatch<UseEnhanceSpeechActions>;
